import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";

/**
 * Admin panel: override the default leave entitlements (annual + sick). Blank
 * inputs fall through: employee override → level policy → tenant defaults →
 * built-in Oman defaults (30 annual, 70 sick).
 */
export default function EntitlementsPanel() {
  const [policy, setPolicy] = useState({ defaults: {}, by_level: {}, builtin_defaults: { annual: 30, sick: 70 } });
  const [levels, setLevels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newLvl, setNewLvl] = useState("");

  const load = async () => {
    try {
      const [pol, hier] = await Promise.all([
        api.get("/hr/entitlements/policy").then((r) => r.data),
        api.get("/hierarchy/levels").then((r) => r.data).catch(() => ({ levels: [] })),
      ]);
      setPolicy(pol);
      setLevels(hier.levels || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const setDefault = (key, val) => {
    const v = val === "" ? undefined : Number(val);
    setPolicy((p) => ({ ...p, defaults: { ...(p.defaults || {}), [key]: v } }));
  };
  const setLevel = (lvl, key, val) => {
    const v = val === "" ? undefined : Number(val);
    setPolicy((p) => {
      const nb = { ...(p.by_level || {}) };
      nb[lvl] = { ...(nb[lvl] || {}), [key]: v };
      // Drop keys with undefined values
      Object.keys(nb[lvl]).forEach((k) => { if (nb[lvl][k] === undefined) delete nb[lvl][k]; });
      if (Object.keys(nb[lvl]).length === 0) delete nb[lvl];
      return { ...p, by_level: nb };
    });
  };
  const addLevelRow = () => {
    const n = newLvl.trim();
    if (!n) return;
    setPolicy((p) => ({ ...p, by_level: { ...(p.by_level || {}), [n]: {} } }));
    setNewLvl("");
  };
  const removeLevel = (lvl) => {
    setPolicy((p) => {
      const nb = { ...(p.by_level || {}) };
      delete nb[lvl];
      return { ...p, by_level: nb };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Strip undefined defaults
      const defaults = Object.fromEntries(Object.entries(policy.defaults || {}).filter(([, v]) => Number.isFinite(Number(v))));
      const by_level = {};
      for (const [lvl, v] of Object.entries(policy.by_level || {})) {
        const clean = Object.fromEntries(Object.entries(v).filter(([, x]) => Number.isFinite(Number(x))));
        if (Object.keys(clean).length) by_level[lvl] = clean;
      }
      await api.put("/hr/entitlements/policy", { defaults, by_level });
      toast.success("Entitlement policy saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const bd = policy.builtin_defaults || { annual: 30, sick: 70 };
  const knownLevels = Array.from(new Set([...(levels || []), ...Object.keys(policy.by_level || {})]));

  return (
    <section className="border border-zinc-950 mt-8" data-testid="entitlements-panel">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between">
        <span>leave entitlements policy</span>
        <button onClick={load} className="text-white/60 hover:text-white flex items-center gap-1" title="Reload">
          <ArrowsClockwise size={11} />
        </button>
      </div>
      <div className="p-5">
        <p className="text-sm text-zinc-600 mb-4 max-w-2xl">
          Set the tenant default annual and sick days, then optionally raise the ceiling for specific levels. Individual employees can still be nudged higher via the row-level override on the HR Directory. Empty cells inherit from the next tier.
        </p>

        <div className="border border-zinc-200 mb-6">
          <div className="grid grid-cols-[1fr_120px_120px_1fr] gap-0 border-b border-zinc-950 bg-zinc-50 text-xs tracking-wider uppercase">
            <div className="px-3 py-2">Tier</div>
            <div className="px-3 py-2 text-right">Annual</div>
            <div className="px-3 py-2 text-right">Sick</div>
            <div className="px-3 py-2 text-zinc-400">Falls back to</div>
          </div>
          <div className="grid grid-cols-[1fr_120px_120px_1fr] gap-0 items-center border-b border-zinc-100">
            <div className="px-3 py-2 font-medium">Tenant defaults</div>
            <div className="px-3 py-2">
              <input
                type="number"
                min="0"
                max="365"
                value={policy.defaults?.annual ?? ""}
                placeholder={String(bd.annual)}
                onChange={(e) => setDefault("annual", e.target.value)}
                data-testid="ent-default-annual"
                className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm text-right tnum outline-none"
              />
            </div>
            <div className="px-3 py-2">
              <input
                type="number"
                min="0"
                max="365"
                value={policy.defaults?.sick ?? ""}
                placeholder={String(bd.sick)}
                onChange={(e) => setDefault("sick", e.target.value)}
                data-testid="ent-default-sick"
                className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm text-right tnum outline-none"
              />
            </div>
            <div className="px-3 py-2 text-[10px] font-mono text-zinc-400">OMAN LABOR LAW · {bd.annual}A / {bd.sick}S</div>
          </div>

          {knownLevels.map((lvl) => (
            <div key={lvl} className="grid grid-cols-[1fr_120px_120px_1fr] gap-0 items-center border-b border-zinc-100" data-testid={`ent-level-row-${lvl}`}>
              <div className="px-3 py-2 flex items-center gap-2">
                <span className="text-[10px] font-mono text-zinc-400">LVL</span>
                <span className="font-medium">{lvl}</span>
              </div>
              <div className="px-3 py-2">
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={policy.by_level?.[lvl]?.annual ?? ""}
                  placeholder="inherit"
                  onChange={(e) => setLevel(lvl, "annual", e.target.value)}
                  data-testid={`ent-lvl-${lvl}-annual`}
                  className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm text-right tnum outline-none"
                />
              </div>
              <div className="px-3 py-2">
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={policy.by_level?.[lvl]?.sick ?? ""}
                  placeholder="inherit"
                  onChange={(e) => setLevel(lvl, "sick", e.target.value)}
                  data-testid={`ent-lvl-${lvl}-sick`}
                  className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm text-right tnum outline-none"
                />
              </div>
              <div className="px-3 py-2 flex items-center justify-between text-[10px] font-mono">
                <span className="text-zinc-400">TENANT DEFAULT</span>
                <button
                  onClick={() => removeLevel(lvl)}
                  className="text-zinc-400 hover:text-[#D0021B]"
                  title="Remove level override"
                  data-testid={`ent-lvl-remove-${lvl}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-4">
          <input
            value={newLvl}
            onChange={(e) => setNewLvl(e.target.value)}
            placeholder="e.g. Director"
            onKeyDown={(e) => e.key === "Enter" && addLevelRow()}
            data-testid="ent-new-level-input"
            className="flex-1 max-w-sm border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm outline-none"
          />
          <button
            onClick={addLevelRow}
            className="text-xs font-semibold border border-zinc-950 px-3 py-1.5 hover:bg-zinc-950 hover:text-white flex items-center gap-1"
            data-testid="ent-add-level-btn"
          >
            <Plus size={12} /> ADD LEVEL OVERRIDE
          </button>
        </div>

        <button
          onClick={save}
          disabled={saving}
          data-testid="ent-save-btn"
          className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2 hover:bg-[#FF3E00] disabled:opacity-40 flex items-center gap-1"
        >
          <FloppyDisk size={12} /> {saving ? "SAVING…" : "SAVE POLICY"}
        </button>
      </div>
    </section>
  );
}
